import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import type { BookNavRegistration } from '@/providers/platform-sidebar-outlet';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

const submenuContentClassName = 'border-sidebar-border border-l pl-[1.375rem]';

const sidebarChapterAccordionTriggerClassName = cn(
    'min-w-0 cursor-pointer',
    'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
    'gap-2 rounded-md px-2 py-1.5 text-xs font-normal hover:no-underline [&>svg]:text-sidebar-foreground',
);

function chapterStorageKey(bookId: string) {
    return `bookChapterSubOpen_${bookId}`;
}

type BookSidebarNavProps = {
    registration: BookNavRegistration;
    showNestedNav: boolean;
};

export function BookSidebarNav({ registration, showNestedNav }: BookSidebarNavProps) {
    const { book, chapters, activeSectionId, headings, scrollToHeading } = registration;

    const activeChapterId = useMemo(() => {
        if (!activeSectionId) return null;
        for (const chapter of chapters) {
            if (chapter.sections.some(s => s.id === activeSectionId)) {
                return chapter.id;
            }
        }
        return null;
    }, [chapters, activeSectionId]);

    const [chapterSubOpen, setChapterSubOpen] = useState<Record<string, boolean>>(() => {
        try {
            const raw = localStorage.getItem(chapterStorageKey(book.id));
            return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
        } catch {
            return {};
        }
    });

    function isChapterOpen(chapterId: string) {
        const stored = chapterSubOpen[chapterId];
        if (stored === false) {
            return false;
        }
        return stored === true || chapterId === activeChapterId;
    }

    function handleChapterAccordionValueChange(value: string[]) {
        const next: Record<string, boolean> = { ...chapterSubOpen };
        for (const chapter of chapters) {
            next[chapter.id] = value.includes(chapter.id);
        }
        setChapterSubOpen(next);
        localStorage.setItem(chapterStorageKey(book.id), JSON.stringify(next));
    }

    const bookTocPath = `/books/${encodeURIComponent(book.id)}`;

    return (
        <div className={submenuContentClassName}>
            <SidebarMenu>
                <SidebarMenuItem>
                    <SidebarMenuButton
                        asChild
                        tooltip={book.title}
                    >
                        <Link to={bookTocPath}>
                            <span
                                className="truncate font-medium"
                                title={book.title}
                            >
                                {book.title}
                            </span>
                        </Link>
                    </SidebarMenuButton>
                </SidebarMenuItem>
            </SidebarMenu>
            <Accordion
                type="multiple"
                className="w-full"
                value={chapters.filter(chapter => isChapterOpen(chapter.id)).map(chapter => chapter.id)}
                onValueChange={handleChapterAccordionValueChange}
            >
                {chapters.map(chapter => {
                    const isActiveChapter = activeChapterId === chapter.id;
                    const highlightChapterTrigger = isActiveChapter && !isChapterOpen(chapter.id);

                    const sectionLinks = chapter.sections.map(section => {
                        const sectionPath = `/books/${encodeURIComponent(book.id)}/${encodeURIComponent(section.id)}`;
                        const isActiveSection = activeSectionId === section.id;
                        const headingsForSection = isActiveSection ? headings : [];

                        return (
                            <SidebarMenuSubItem key={section.id}>
                                <SidebarMenuSubButton
                                    asChild
                                    size="sm"
                                    isActive={isActiveSection}
                                >
                                    <Link
                                        to={sectionPath}
                                        title={`${section.number} ${section.title}`}
                                    >
                                        <span className="truncate">
                                            {section.number} {section.title}
                                        </span>
                                    </Link>
                                </SidebarMenuSubButton>
                                {headingsForSection.length > 0 && (
                                    <SidebarMenuSub className="mx-0 p-0">
                                        {headingsForSection.map((h, index) => (
                                            <SidebarMenuSubItem key={h.id ?? `heading-${index}`}>
                                                <SidebarMenuSubButton
                                                    size="sm"
                                                    href="#"
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        scrollToHeading?.(h.id, index);
                                                    }}
                                                    style={{
                                                        paddingLeft: Math.min(5 + (h.level - 1) * 5, 20),
                                                    }}
                                                >
                                                    <span
                                                        className="truncate"
                                                        title={h.title}
                                                    >
                                                        {h.title}
                                                    </span>
                                                </SidebarMenuSubButton>
                                            </SidebarMenuSubItem>
                                        ))}
                                    </SidebarMenuSub>
                                )}
                            </SidebarMenuSubItem>
                        );
                    });

                    return (
                        <AccordionItem
                            key={chapter.id}
                            value={chapter.id}
                            className="border-none"
                        >
                            <AccordionTrigger
                                title={`Chapter ${chapter.number}: ${chapter.title}`}
                                className={cn(
                                    sidebarChapterAccordionTriggerClassName,
                                    highlightChapterTrigger && 'bg-sidebar-accent text-sidebar-accent-foreground',
                                )}
                            >
                                <span className="truncate">
                                    Ch. {chapter.number}: {chapter.title}
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className={cn('pb-0', !showNestedNav && 'hidden')}>
                                <SidebarMenuSub>{sectionLinks}</SidebarMenuSub>
                            </AccordionContent>
                        </AccordionItem>
                    );
                })}
            </Accordion>
        </div>
    );
}
